import classNames from "classnames";
import React, { useEffect, useState } from "react";
import HandleClicksOutside from "../HandleClicksOutside";
import Divider from "./Divider";
import Icon from "./Icon";
import ListFilterGroupUnit from "./ListFilterGroupUnit";
import "./ListFilter.css";
import { Flex } from "./Flex";
import Label from "./Label";

export type ListFilterGroupItem<T> = {
    data: T;
    selected: boolean;
    count: number;
    children?: ListFilterGroupItem<T>[];
};

type ListFilterGroup<T> = {
    title?: string;
    items: ListFilterGroupItem<T>[];
    onChange: (selected: T[]) => void;
};

type ListFilterGroupProps<T> = {
    title: string;
    filters: ListFilterGroup<T>[];
    position: "bottom-right" | "bottom-left";
    showAllFiltersWithoutCount?: boolean;
};

function ListFilterGroup<T extends { id: string; name: string }>(
    props: ListFilterGroupProps<T>,
) {
    const { title, filters, position, showAllFiltersWithoutCount } = props;

    const [open, setOpen] = useState(false);

    const [filterStateEnabled, setFilterStateEnabled] = useState<boolean>(
        filters.flatMap((filter) => filter.items).find((i) => i.selected)?.selected ||
            false,
    );

    useEffect(() => {
        const allItems = filters.flatMap((filter) => filter.items);
        setFilterStateEnabled(allItems.find((i) => i.selected)?.selected || false);
    }, [filters]);

    return (
        <HandleClicksOutside
            className={classNames({
                "list-filter": true,
                "list-filter--open": open,
                "list-filter__bottom-right": position === "bottom-right",
                "list-filter__bottom-left": position === "bottom-left",
            })}
            onClickOutside={() => {
                setOpen(false);
            }}
            disabled={!open}
        >
            {" "}
            <button
                type="button"
                className="list-filter__title"
                onClick={() => {
                    setOpen(!open);
                }}
            >
                <Flex.Row gap="small">
                    {title && <Label.Medium.Normal>{title}</Label.Medium.Normal>}
                    {filterStateEnabled ? (
                        <Icon.FilterList.Medium.P50 />
                    ) : (
                        <Icon.FilterList.Medium.N50 />
                    )}
                </Flex.Row>
            </button>
            {open && (
                <div className="list-filter__list">
                    {filters.map((filter, index) => (
                        <div key={filter.title}>
                            <ListFilterGroupUnit<T>
                                title={filter.title}
                                items={filter.items}
                                showAllFiltersWithoutCount={showAllFiltersWithoutCount}
                                onChange={filter.onChange}
                            />
                            {index < filters.length - 1 && <Divider />}
                        </div>
                    ))}
                </div>
            )}
        </HandleClicksOutside>
    );
}

export default ListFilterGroup;
